import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { FooterLink } from "@/components/NavLink";
import { useContactModal } from "@/components/ContactModal";
import { cn } from "@/lib/utils";

const Footer = () => {
  const navigate = useNavigate();
  const { open: openContact } = useContactModal();
  const [openSection, setOpenSection] = useState<string | null>(null);

  const goToModuleSelection = (slug: string) => {
    navigate("/demo", { state: { openModule: slug } });
  };

  const exploreItemClass =
    "text-left text-muted-foreground font-body text-xs md:text-sm hover:text-foreground transition-colors break-words bg-transparent border-0 p-0 cursor-pointer";

  const toggleSection = (title: string) => {
    setOpenSection((prev) => (prev === title ? null : title));
  };

  const sections = [
    {
      title: "Visit",
      content: (
        <p className="text-muted-foreground font-body text-xs md:text-sm leading-relaxed break-words">
          xilancia GmbH<br />
          The Circle 6<br />
          8058 Zürich<br />
          Switzerland
        </p>
      ),
    },
    {
      title: "Follow us",
      content: (
        <div className="flex flex-col gap-2">
          <FooterLink href="https://www.fanpictor.com" external>Website</FooterLink>
          <FooterLink href="https://www.linkedin.com/company/fanpictor" external>LinkedIn</FooterLink>
          <FooterLink href="https://www.instagram.com/fanpictor/" external>Instagram</FooterLink>
          <FooterLink href="https://www.facebook.com/Fanpictor/" external>Facebook</FooterLink>
          <FooterLink href="https://x.com/fanpictor" external>X</FooterLink>
        </div>
      ),
    },
    {
      title: "Legal",
      content: (
        <div className="flex flex-col gap-2">
          <FooterLink href="https://fanpictor.com/legal-notice.php" external>Legal Notice</FooterLink>
          <FooterLink href="https://fanpictor.com/disclaimer.php" external>Disclaimer</FooterLink>
          <FooterLink href="https://fanpictor.com/privacy-policy.php" external>Privacy Policy</FooterLink>
        </div>
      ),
    },
  ];

  return (
    <footer className="relative z-10 px-4 py-6 md:px-12 md:py-10">
      {/* Desktop — unchanged */}
      <div className="hidden md:block">
        <div className="mx-auto grid grid-cols-1 sm:grid-cols-3 gap-x-12 lg:gap-x-16 gap-y-8 w-full max-w-4xl items-start">
          <div className="flex flex-col gap-2 min-w-0">
            <h4 className="font-display text-[10px] md:text-xs font-bold tracking-[0.15em] uppercase text-foreground mb-1">Visit</h4>
            <p className="text-muted-foreground font-body text-xs md:text-sm leading-relaxed break-words">
              xilancia GmbH<br />
              The Circle 6<br />
              8058 Zürich<br />
              Switzerland
            </p>
          </div>

          <div className="flex flex-col gap-2 min-w-0">
            <h4 className="font-display text-[10px] md:text-xs font-bold tracking-[0.15em] uppercase text-foreground mb-1">Follow us</h4>
            <FooterLink href="https://www.fanpictor.com" external>Website</FooterLink>
            <FooterLink href="https://www.linkedin.com/company/fanpictor" external>LinkedIn</FooterLink>
            <FooterLink href="https://www.instagram.com/fanpictor/" external>Instagram</FooterLink>
            <FooterLink href="https://www.facebook.com/Fanpictor/" external>Facebook</FooterLink>
            <FooterLink href="https://x.com/fanpictor" external>X</FooterLink>
          </div>

          <div className="flex flex-col gap-2 min-w-0">
            <h4 className="font-display text-[10px] md:text-xs font-bold tracking-[0.15em] uppercase text-foreground mb-1">Legal</h4>
            <FooterLink href="https://fanpictor.com/legal-notice.php" external>Legal Notice</FooterLink>
            <FooterLink href="https://fanpictor.com/disclaimer.php" external>Disclaimer</FooterLink>
            <FooterLink href="https://fanpictor.com/privacy-policy.php" external>Privacy Policy</FooterLink>
          </div>
        </div>
      </div>

      {/* Mobile — collapsible accordion */}
      <div className="md:hidden max-w-5xl mx-auto">
        {sections.map((section) => (
          <div key={section.title} className="border-b border-foreground/10">
            <button
              type="button"
              onClick={() => toggleSection(section.title)}
              className="flex w-full items-center justify-between py-4 min-h-[52px]"
              aria-expanded={openSection === section.title}
            >
              <h4 className="font-display text-xs font-bold tracking-[0.15em] uppercase text-foreground">
                {section.title}
              </h4>
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300",
                  openSection === section.title && "rotate-180"
                )}
              />
            </button>
            <div
              className={cn(
                "grid transition-all duration-300 ease-out",
                openSection === section.title
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0"
              )}
            >
              <div className="overflow-hidden pb-4">
                {section.content}
              </div>
            </div>
          </div>
        ))}
      </div>
    </footer>
  );
};

export default Footer;
